// REQ-P1-06b: 共通チャット通知バブル固定の契約型付きAPI。

import { api, type paths } from './client';

type AnnouncementResponse = NonNullable<
  paths['/artist/messages/announcements']['get']['responses'][200]['content']
>['application/json'];
type AnnouncementMutationRequest = NonNullable<
  paths['/artist/messages/announcements/{messageId}']['put']['requestBody']
>['content']['application/json'];

type ChatAnnouncementScope = 'individual' | 'event' | 'group';
type ChatAnnouncementMode = 'admin' | 'artist';

type ChatAnnouncementParams = {
  mode?: ChatAnnouncementMode;
  scope?: ChatAnnouncementScope;
  artistId?: string;
  eventId?: string;
  threadId?: string;
  messageId?: string;
};

type RequestOptions = {
  signal?: AbortSignal;
};

function requireParam(value: string | undefined, name: string): string {
  if (!value) throw new Error(`${name} is required`);
  return value;
}

async function readOptionalJson(data: unknown, response: Response): Promise<Record<string, unknown>> {
  if (data && typeof data === 'object') return data as Record<string, unknown>;
  try {
    const json = await response.clone().json();
    return json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

async function ensurePayloadOk(
  data: unknown,
  response: Response
): Promise<Record<string, unknown>> {
  const payload = await readOptionalJson(data, response);
  if (response.ok && payload?.success !== false) return payload;
  const error = new Error(String(payload?.message || payload?.error || `HTTP ${response.status}`));
  (error as Error & { status?: number; payload?: Record<string, unknown> }).status = response.status;
  (error as Error & { status?: number; payload?: Record<string, unknown> }).payload = payload;
  throw error;
}

const emptyMutationBody = (): AnnouncementMutationRequest => ({}) as AnnouncementMutationRequest;

export async function fetchChatAnnouncements(
  params: ChatAnnouncementParams,
  options: RequestOptions = {}
): Promise<AnnouncementResponse> {
  const mode = params.mode || 'admin';
  const scope = params.scope || 'individual';

  if (scope === 'group') {
    const threadId = requireParam(params.threadId, 'threadId');
    const result =
      mode === 'artist'
        ? await api.GET('/artist/group-chat/threads/{threadId}/announcements', {
            params: { path: { threadId } },
            signal: options.signal,
          })
        : await api.GET('/admin/group-chat/threads/{threadId}/announcements', {
            params: { path: { threadId } },
            signal: options.signal,
          });
    return ensurePayloadOk(result.data, result.response) as Promise<AnnouncementResponse>;
  }

  if (scope === 'event') {
    const eventId = requireParam(params.eventId, 'eventId');
    const { data, response } = await api.GET('/event-chat/events/{eventId}/announcements', {
      params: { path: { eventId } },
      signal: options.signal,
    });
    return ensurePayloadOk(data, response) as Promise<AnnouncementResponse>;
  }

  if (mode === 'artist') {
    const { data, response } = await api.GET('/artist/messages/announcements', {
      signal: options.signal,
    });
    return ensurePayloadOk(data, response) as Promise<AnnouncementResponse>;
  }

  const artistId = requireParam(params.artistId, 'artistId');
  const { data, response } = await api.GET('/admin/messages/{artistId}/announcements', {
    params: { path: { artistId } },
    signal: options.signal,
  });
  return ensurePayloadOk(data, response) as Promise<AnnouncementResponse>;
}

export async function pinChatAnnouncement(
  params: ChatAnnouncementParams
): Promise<Record<string, unknown>> {
  const mode = params.mode || 'admin';
  const scope = params.scope || 'individual';
  const messageId = requireParam(params.messageId, 'messageId');
  const body = emptyMutationBody();

  if (scope === 'group') {
    const threadId = requireParam(params.threadId, 'threadId');
    const result =
      mode === 'artist'
        ? await api.PUT('/artist/group-chat/threads/{threadId}/announcements/{messageId}', {
            params: { path: { threadId, messageId } },
            body,
          })
        : await api.PUT('/admin/group-chat/threads/{threadId}/announcements/{messageId}', {
            params: { path: { threadId, messageId } },
            body,
          });
    return ensurePayloadOk(result.data, result.response);
  }

  if (scope === 'event') {
    const eventId = requireParam(params.eventId, 'eventId');
    const { data, response } = await api.PUT(
      '/event-chat/events/{eventId}/announcements/{messageId}',
      {
        params: { path: { eventId, messageId } },
        body,
      }
    );
    return ensurePayloadOk(data, response);
  }

  if (mode === 'artist') {
    const { data, response } = await api.PUT('/artist/messages/announcements/{messageId}', {
      params: { path: { messageId } },
      body,
    });
    return ensurePayloadOk(data, response);
  }

  const artistId = requireParam(params.artistId, 'artistId');
  const { data, response } = await api.PUT(
    '/admin/messages/{artistId}/announcements/{messageId}',
    {
      params: { path: { artistId, messageId } },
      body,
    }
  );
  return ensurePayloadOk(data, response);
}

export async function unpinChatAnnouncement(
  params: ChatAnnouncementParams
): Promise<Record<string, unknown>> {
  const mode = params.mode || 'admin';
  const scope = params.scope || 'individual';
  const messageId = requireParam(params.messageId, 'messageId');

  if (scope === 'group') {
    const threadId = requireParam(params.threadId, 'threadId');
    const result =
      mode === 'artist'
        ? await api.DELETE('/artist/group-chat/threads/{threadId}/announcements/{messageId}', {
            params: { path: { threadId, messageId } },
          })
        : await api.DELETE('/admin/group-chat/threads/{threadId}/announcements/{messageId}', {
            params: { path: { threadId, messageId } },
          });
    return ensurePayloadOk(result.data, result.response);
  }

  if (scope === 'event') {
    const eventId = requireParam(params.eventId, 'eventId');
    const { data, response } = await api.DELETE(
      '/event-chat/events/{eventId}/announcements/{messageId}',
      {
        params: { path: { eventId, messageId } },
      }
    );
    return ensurePayloadOk(data, response);
  }

  if (mode === 'artist') {
    const { data, response } = await api.DELETE('/artist/messages/announcements/{messageId}', {
      params: { path: { messageId } },
    });
    return ensurePayloadOk(data, response);
  }

  const artistId = requireParam(params.artistId, 'artistId');
  const { data, response } = await api.DELETE(
    '/admin/messages/{artistId}/announcements/{messageId}',
    {
      params: { path: { artistId, messageId } },
    }
  );
  return ensurePayloadOk(data, response);
}
